/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import cx from 'classnames';
import { isString, isFunction } from 'lodash';

import styles from './Image.scss';

const { useCallback, useEffect, useRef, useState } = React;
const ASSETS = process.env.ASSETS;
const defaultImageSrc = `${ASSETS}/content_image_placeholder.png`;

type TBaseProps = React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export interface IImageProps extends TBaseProps {
  fallbackSrc?: string;
  onSizeDetected?(naturalWidth: number, naturalHeight: number);
  onLoad?();
  onError?();
}

export const Image: React.RefForwardingComponent<HTMLImageElement, IImageProps> = React.memo(
  React.forwardRef((props: IImageProps, forwardRef: React.Ref<HTMLImageElement>) => {
    const {
      className,
      fallbackSrc,
      onError: onErrorProp,
      onLoad: onLoadProp,
      onSizeDetected,
      src,
      ...restProps
    } = props;

    const innerRef = useRef<HTMLImageElement>(null);

    // Combined ref handler that works with both callback refs and RefObject
    const combinedRef = useCallback(
      (element: HTMLImageElement | null) => {
        // Always update our internal ref
        innerRef.current = element;

        // Handle the forwarded ref
        if (forwardRef) {
          if (typeof forwardRef === 'function') {
            // Callback ref
            forwardRef(element);
          } else {
            // RefObject
            (forwardRef as React.MutableRefObject<HTMLImageElement | null>).current = element;
          }
        }
      },
      [forwardRef],
    );

    const [isFailed, setIsFailed] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const poll = useRef<number>(null);

    /**
     * Handler for failed to load image.
     */
    const onError = useCallback(() => {
      setIsFailed(true);
      setIsCompleted(true);
      if (isFunction(onErrorProp)) {
        onErrorProp();
      }
    }, [onErrorProp]);

    /**
     * Callback for when placeholder image is loaded.
     */
    const onLoadPlaceholder = useCallback(() => {
      const imageRef = innerRef.current;
      // Guard the clear by checking if poll.current is truthy before calling clearInterval
      if (poll.current) {
        window.clearInterval(poll.current);
        poll.current = null; // Set to null immediately after clearing to keep handle state accurate
      }
      if (isFunction(onSizeDetected)) {
        onSizeDetected(imageRef?.naturalWidth, imageRef?.naturalHeight);
      }
    }, [onSizeDetected]);

    /**
     * Callback for when image is loaded.
     */
    const onLoad = useCallback(() => {
      setIsCompleted(true);
      if (isFunction(onLoadProp)) {
        onLoadProp();
      }
      // Call onSizeDetected directly to avoid dependency issues
      const imageRef = innerRef.current;
      // Guard the clear by checking if poll.current is truthy before calling clearInterval
      if (poll.current) {
        window.clearInterval(poll.current);
        poll.current = null; // Set to null immediately after clearing to keep handle state accurate
      }
      if (isFunction(onSizeDetected)) {
        onSizeDetected(imageRef?.naturalWidth, imageRef?.naturalHeight);
      }
    }, [onLoadProp, onSizeDetected]);

    // New src, set statuses to default
    useEffect(() => {
      setIsFailed(false);
      setIsCompleted(false);
    }, [src]);

    // trigger onLoad if image is already loaded
    // this happens because 'onLoad' handler is attached once js is loaded
    // which could happen after image is loaded for server-side rendering
    // please refer to: https://stackoverflow.com/questions/39777833/image-onload-event-in-isomorphic-universal-react-register-event-after-image-is
    useEffect(() => {
      const image = innerRef.current;
      if (image && image.complete) {
        if (image.naturalWidth === 0) {
          onError();
        } else if (image.complete) {
          onLoad();
        }
      }
    }, [onError, onLoad]);

    // Start/clear polling to resize
    useEffect(() => {
      if (isFailed && poll.current !== null) {
        window.clearInterval(poll.current);
        poll.current = null;
      } else if (!isFailed && poll.current === null) {
        poll.current = window.setInterval(() => {
          // Read innerRef.current inside the callback to avoid stale ref capture
          const img = innerRef.current;
          if (img && img.naturalWidth && img.naturalHeight) {
            // Ensure clearInterval uses the latest poll.current value before nulling it
            const currentPoll = poll.current;
            window.clearInterval(currentPoll);
            poll.current = null; // Reset the handle
            if (isFunction(onSizeDetected)) {
              onSizeDetected(img.naturalWidth, img.naturalHeight);
            }
          }
        }, 100);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFailed]); // Removed onSizeDetected and ref from dependencies

    // Cleanup interval on unmount
    useEffect(() => {
      return () => {
        if (poll.current) {
          window.clearInterval(poll.current);
          poll.current = null;
        }
      };
    }, []);

    // in case src is not string
    const imageSrc = src || fallbackSrc;

    if (isFailed && isString(fallbackSrc)) {
      return (
        <img
          {...restProps}
          ref={combinedRef}
          src={fallbackSrc}
          onLoad={onLoadPlaceholder}
          className={cx(className, styles.Image, {
            [styles.completed]: isCompleted,
          })}
        />
      );
    }

    return (
      <img
        {...restProps}
        src={imageSrc}
        ref={combinedRef}
        onError={onError}
        onLoad={onLoad}
        className={cx(className, styles.Image, {
          [styles.completed]: isCompleted,
        })}
      />
    );
  }),
);

Image.defaultProps = {
  fallbackSrc: defaultImageSrc as never,
  onSizeDetected: (() => undefined) as never,
  onLoad: (() => undefined) as never,
  onError: (() => undefined) as never,
} as never;
Image.displayName = 'Image';
